import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { BarChart3, Percent, Crown, Megaphone, Coins, Banknote, ShoppingBag } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";

const ADMIN_ROLES = ["super_admin", "admin_lcs"];
const PERIODS = [
  { key: "30d", label: "30 jours" },
  { key: "90d", label: "90 jours" },
  { key: "12m", label: "12 mois" },
  { key: "all", label: "Tout" },
];
const SOURCE_META = [
  { key: "commissions", label: "Commissions", color: "#15B8A6" },
  { key: "subscriptions", label: "Abonnements Pro", color: "#1D6FC4" },
  { key: "ads", label: "Publicité", color: "#F97316" },
  { key: "credits", label: "Crédits", color: "#0B2B4E" },
];
const money = (v) => `${(v ?? 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;

export default function Revenues() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const isAdmin = user && ADMIN_ROLES.includes(user.role);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
    else if (!authLoading && user && !isAdmin) navigate("/dashboard", { replace: true });
  }, [user, authLoading, isAdmin, navigate]);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get(`/revenue/overview?period=${period}`);
      setData(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [period]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (!user || !isAdmin) return null;

  const t = data?.totals;
  const pieData = SOURCE_META
    .map((s) => ({ name: s.label, value: t ? t[s.key] : 0, color: s.color }))
    .filter((d) => d.value > 0);

  return (
    <div data-testid="revenues-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy">
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-brand-turquoise" />
              Revenus NEXA
            </h1>
            <p className="text-white/70 text-sm mt-1">
              Vue consolidée de la rentabilité : commissions, abonnements, publicité et crédits.
            </p>
          </div>
          <div data-testid="period-selector" className="flex gap-1.5 bg-white/10 rounded-lg p-1">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                data-testid={`period-${p.key}`}
                onClick={() => setPeriod(p.key)}
                className={`text-xs font-semibold rounded-md px-3.5 py-2 transition-colors ${
                  period === p.key ? "bg-brand-turquoise text-white" : "text-white/70 hover:text-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div data-testid="revenues-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}

        {!data ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div data-testid="revenues-total-card" className="bg-brand-navy rounded-xl p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs font-medium text-white/60 uppercase tracking-wide">Revenus plateforme ({PERIODS.find((p) => p.key === period)?.label})</p>
                <p data-testid="revenues-platform-total" className="text-4xl font-extrabold text-brand-turquoise mt-1">{money(t.platform_total)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium text-white/60 uppercase tracking-wide">Volume brut transigé (GMV)</p>
                <p data-testid="revenues-gmv" className="text-2xl font-bold text-white mt-1">{money(t.gmv)}</p>
                <p className="text-xs text-white/50 mt-0.5">{data.counts.gmv_transactions} transactions Market/Factures</p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {SOURCE_META.map((s) => (
                <div key={s.key} className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{s.label}</span>
                    {s.key === "commissions" && <Percent className="w-4 h-4" style={{ color: s.color }} />}
                    {s.key === "subscriptions" && <Crown className="w-4 h-4" style={{ color: s.color }} />}
                    {s.key === "ads" && <Megaphone className="w-4 h-4" style={{ color: s.color }} />}
                    {s.key === "credits" && <Coins className="w-4 h-4" style={{ color: s.color }} />}
                  </div>
                  <p data-testid={`revenues-${s.key}`} className="text-2xl font-bold text-brand-navy">{money(t[s.key])}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {s.key === "commissions" && `${data.counts.commissioned_sales} ventes commissionnées`}
                    {s.key === "subscriptions" && `${data.counts.subscriptions} abonnements`}
                    {s.key === "ads" && `${data.counts.ads_campaigns} campagnes`}
                    {s.key === "credits" && `${data.counts.credits} achats`}
                  </p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Retraits versés aux membres</span>
                  <Banknote className="w-4 h-4 text-red-500" />
                </div>
                <p data-testid="revenues-withdrawals" className="text-2xl font-bold text-red-600">{money(t.withdrawals)}</p>
                <p className="text-xs text-slate-400 mt-1">Sorties de fonds approuvées sur la période</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Taux de capture (revenus / GMV)</span>
                  <ShoppingBag className="w-4 h-4 text-brand-blue" />
                </div>
                <p data-testid="revenues-take-rate" className="text-2xl font-bold text-brand-navy">
                  {t.gmv > 0 ? `${((t.commissions / t.gmv) * 100).toFixed(1)} %` : "—"}
                </p>
                <p className="text-xs text-slate-400 mt-1">Commissions prélevées sur le volume brut</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-brand-navy mb-4">
                Évolution des revenus par {data.granularity === "month" ? "mois" : "jour"}
              </h2>
              {data.series.length === 0 ? (
                <p data-testid="revenues-series-empty" className="text-sm text-slate-400 text-center py-10">
                  Aucun revenu enregistré sur cette période.
                </p>
              ) : (
                <div data-testid="revenues-chart" className="h-72">
                  <ResponsiveContainer width="100%" height="100%" debounce={50}>
                    <BarChart data={data.series} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} $`} width={70} />
                      <Tooltip formatter={(v, name) => [money(v), SOURCE_META.find((s) => s.key === name)?.label || name]} />
                      <Legend formatter={(v) => SOURCE_META.find((s) => s.key === v)?.label || v} />
                      {SOURCE_META.map((s) => (
                        <Bar key={s.key} dataKey={s.key} stackId="revenue" fill={s.color} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-brand-navy mb-4">Répartition par source</h2>
              {pieData.length === 0 ? (
                <p data-testid="revenues-pie-empty" className="text-sm text-slate-400 text-center py-10">
                  Aucune source de revenus sur cette période.
                </p>
              ) : (
                <div data-testid="revenues-pie" className="h-72">
                  <ResponsiveContainer width="100%" height="100%" debounce={50}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={70} outerRadius={110} paddingAngle={3}>
                        {pieData.map((d) => (
                          <Cell key={d.name} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, name) => [money(v), name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
